import React, { useState, useEffect, useCallback } from 'react';
import { inject, observer } from 'mobx-react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { fetch } from '../../utils/api-wrapper';
import SCIMProvisioningSection from '../../components/client-form/SCIMProvisioningSection';
import GroupRoleMappingSection from '../../components/client-form/GroupRoleMappingSection';

// Dedicated per-client page for identity-provider provisioning, reached from
// the Users admin panel. Moved off the client edit modal, which was already
// crowded. Hosts the SCIM provisioning panel (self-saving via its own action
// endpoints) and the group->role mapping editor (persisted with the page's
// Save, which writes config.group_role_map through the normal client update).
const ScimProvisioning = ({ data }) => {
    const t = (key) => data.t.read(key);
    const { clientId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // AdminUsers navigates here with the already-loaded client in router state,
    // so the common path skips a round-trip. Fall back to fetching the list on
    // a direct URL load / refresh (no state).
    const preloaded = location.state?.client;
    const hasPreloaded = !!preloaded && preloaded._id === clientId;

    const [client, setClient] = useState(hasPreloaded ? preloaded : null);
    const [loading, setLoading] = useState(!hasPreloaded);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState(null); // { type: 'success' | 'error', msg }

    useEffect(() => {
        if (hasPreloaded) return;
        fetch('/clients/', 'GET', {},
            (res) => {
                setClient((res.data.clients || []).find((c) => c._id === clientId) || null);
                setLoading(false);
            },
            () => setLoading(false),
        );
    }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Mirrors the client-form's updateField contract so the reused
    // GroupRoleMappingSection edits config in this page's local state.
    const updateField = useCallback((section, value, key) => {
        setClient((prev) => (prev ? { ...prev, [section]: { ...prev[section], [key]: value } } : prev));
    }, []);

    const saveMapping = () => {
        if (!client) return;
        setSaving(true);
        setStatus(null);
        // PUT /clients/<id> expects a multipart body with a JSON-encoded
        // `client` field, matching admin-clients.js:updateClient.
        const formData = new FormData();
        formData.append('client', JSON.stringify({ name: client.name, config: client.config }));
        fetch(`/clients/${client._id}`, 'PUT', formData,
            () => {
                setSaving(false);
                setStatus({ type: 'success', msg: t('scim_provisioning_page.saved') });
            },
            () => {
                setSaving(false);
                setStatus({ type: 'error', msg: t('scim_provisioning_page.save_error') });
            },
        );
    };

    return (
        <div className="p-4">
            <button type="button" className="btn btn-link p-0 mb-3" onClick={() => navigate('/admin/users')}>
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />{t('scim_provisioning_page.back')}
            </button>

            {loading ? (
                <i>{t('please_wait')}...</i>
            ) : !client ? (
                <div className="alert alert-danger">{t('scim_provisioning_page.client_not_found')}</div>
            ) : (
                <>
                    <h3 className="mb-1">{t('scim_provisioning_page.title')}</h3>
                    <p className="text-muted">{client.name}</p>

                    <SCIMProvisioningSection updatedClient={client} data={data} />
                    <GroupRoleMappingSection updatedClient={client} data={data} updateField={updateField} />

                    {status && (
                        <div className={`alert ${status.type === 'success' ? 'alert-success' : 'alert-danger'} py-2 mt-3`}>
                            {status.msg}
                        </div>
                    )}
                    <small className="text-muted d-block mt-3">{t('scim_provisioning_page.save_mapping_warning')}</small>
                    <button type="button" className="btn btn-primary mt-2" disabled={saving} onClick={saveMapping}>
                        {t('scim_provisioning_page.save_mapping')}
                    </button>
                </>
            )}
        </div>
    );
};

export default inject('data')(observer(ScimProvisioning));
